import {Box, CircularProgress} from '@mui/material';
import React, {FunctionComponent, useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, useSearchParams} from 'react-router';
import {
  errorBigIcon,
  iconNotifyAlertTriangle,
  iconNotifyWarningInfo,
  toastSuccess
} from '../../../../assets/icons/icons';
import {Button, Chip, Typography, WhiteCard} from '../../../../components/StyledComponents';
import {
  EPaymentRequiredType,
  EPlatformsNames,
  ESubscriptionPlans,
  ETrialStatus,
  SubscriptionMockData
} from '../../../../models/consts';
import {IError, IPaymentRequiredPoppup, PlatformProps} from '../../../../models/inner-models';
import Platform from '../../../../models/platforms-data';
import {
  backupShopifyItem,
  fetchShopifyAll,
  fetchShopifyUser,
  getShopifyBackupsDate,
  restoreAllShopifyBackup
} from '../../../../store/actions';
import {useAppDispatch, useAppSelector} from '../../../../store/hooks';
import {
  getIsTestUser,
  getShopifyAppInstallSelector,
  getShopifyConfirmedSelector,
  getShopifyUsersSelector,
  isShopifyUsersLoadingSelector,
  isSubUser,
  loadingUser,
  roleByApp,
  shopifySubscription,
  shopifyTrialHoursLeft,
  shopifyTrialPlanQuantity,
  shopifyTrialQuantityLeft,
  shopifyTrialStatus
} from '../../../../store/selectors';
import getSubInfo from '../../../../utils/platformSubscriptionInfo';
import withRedirectAfterLogout from '../../../../utils/withRedirectAfterLogout';
import '../../style.scss';
import NoBackupsBlock from "../../../../components/Blocks/NoItemsBlock/NoItemsBlock";
import PaymentRequiredPoppup from "../../../../components/Dialogs/PaymentRequiredPoppup/PaymentRequiredPoppup";
import Swal from "sweetalert2";
import {decoratePopUpMessage} from "../../../../utils/popUpTextDecorator";
import Modal from "../../../../components/Dialogs/Modal/Modal";
import NotionUserAddEdit from "../ShopifyUserAddEditView/ShopifyUserAddEdit";
import DatabaseIcoInvert from "../../../../assets/icons/CustomSvg/DatabaseIcoInvert";
import {
  BackupBadges,
  BadgesAppWasDeleted,
  BadgesIFrameMode,
  BadgesSubscription,
  BadgesTrial2
} from "../../../../components/Badges/Badges";
import NotionTutorial from './ShopifyTutorial/ShopifyTutorial';
import CustomTimerTooltip from "../../../../styles/components/CustomTimerTooltip";
import {CustomTooltip} from '../../../../styles/components/CustomTooltip';
import {updateSubscriptionPlanModal} from "../../../../utils/functions/updateSubscriptionPlanModal";
import ExternalStorage from "../../../UserPages/UserSettings/ExternalStorage/ExternalStorage";
import NoSubscriptionBlock from "../../../../components/Blocks/NoSubscriptionBlock/NoSubscriptionBlock";
import {iniFrame} from "../../../../serviceWorker";
import {DataGrid, GridCellParams} from "@mui/x-data-grid";
import UserNotConfirmedBlock from "../../../../components/Blocks/UserNotConfirmedBlock/UserNotConfirmedBlock";
import {useWebSocket} from "../../../../contexts/WebSocketContext";
import {Play} from "iconsax-reactjs";
import ExtStoreIco from "../../../../assets/icons/CustomSvg/ExtStoreIco";

const ShopifyPagesListView: FunctionComponent = () => {
  const {t: translate} = useTranslation();
  const userLoading = useAppSelector(loadingUser);
  // const canUserBackup = useAppSelector(getShopifyCanUserBackup); //todo fix it
  const canUserBackup = true;
  const isTestUser = useAppSelector(getIsTestUser);
  const isLoadingTrelloUsers = useAppSelector(isShopifyUsersLoadingSelector);
  const trialState = useAppSelector(shopifyTrialStatus);
  const trialDays = useAppSelector(shopifyTrialHoursLeft);
  const trialQuantity = useAppSelector(shopifyTrialQuantityLeft);//todo do we need it
  const trialPlanQuantity = useAppSelector(shopifyTrialPlanQuantity);//todo do we need it
  const subInfo = useAppSelector(shopifySubscription);
  const users = useAppSelector(getShopifyUsersSelector);
  const hasUserApp = useAppSelector(getShopifyAppInstallSelector);
  const isShopConfirmed = useAppSelector(getShopifyConfirmedSelector);
  const redirectUrl = users?.[0]?.redirectUrl;
  const isUserSub = useAppSelector(isSubUser);
  const roleApp = useAppSelector(roleByApp);
  const isAdminRole = isTestUser || (isUserSub && roleApp?.shopify === 'admin' && (trialState === 'active' || subInfo?.planName === 'Pro')) || !isUserSub;
  const dispatch = useAppDispatch();
  const platformName = EPlatformsNames.SHOPIFY;
  const platform = Platform[platformName];
  const navigate = useNavigate();
  const [platformInfo, setPlatformInfo] = useState<PlatformProps>(
    {
      platformName: platformName,
      subPlan: SubscriptionMockData[ESubscriptionPlans.NO_SUB],
      items: [],
      users: users || [],
      filteredItems: [],
    },
  );
  const [paymentPoppup, setPaymentPoppup] = useState<IPaymentRequiredPoppup>({
    isOpen: false,
    platformName,
  });
  const [paymentState, setPaymentState] = useState<EPaymentRequiredType>(EPaymentRequiredType.NON);
  const [buyShowed, setBuyShowed] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [showSettings2, setShowSettings2] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const [progressEnd, setProgressEnd] = useState<boolean>(false);
  const [progressItem, setProgressItem] = useState<string>();
  const [isOpenTutorial, setIsOpenTutorial] = useState<boolean>(false);
  const [userCanBackup, setUserCanBackup] = useState<boolean>(false);
  const [userCanRestore, setUserCanRestore] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [backupStatus, setBackupStatus] = useState<string>('initialized');
  const [rows, setRows] = useState([]);
  const appWasDeleted = !!users && !hasUserApp && platformInfo?.items?.length !== 0
  const {onMessage} = useWebSocket();

  useEffect(() => {
    setUserCanBackup(canUserBackup)
  }, [canUserBackup])

  useEffect(() => {
    if (localStorage['showUpdNotionPopup']) {
      setIsOpenTutorial(true)
    }
    const isExt = searchParams.get('isCloudStorageConnected');
    const isAdd = searchParams.get('isWorkspaceConnected');
    const errorMessage = searchParams.get('error_message');

    if (!errorMessage && isAdd) {
      navigate(window.location.pathname, {replace: true})
      Swal.fire({
        title: translate('notifications.titles.success'),
        text: translate('notifications.notion_workspaces.added_workspace'),
        toast: true,
        position: 'top-end',
        timerProgressBar: true,
        showConfirmButton: false,
        showCloseButton: true,
        imageUrl: toastSuccess,
        timer: 3000,
      });
    }

    if (!errorMessage && isExt) {
      setShowSettings2(true)
    }
  }, []);

  useEffect(() => {
    if (!userLoading) {
      const subPlan = getSubInfo(trialState, subInfo, trialDays, platformName, trialQuantity, trialPlanQuantity);

      setPlatformInfo((prev) => ({
        ...prev,
        subPlan,
        users: users || [],
      }));
    }
  }, [trialState, userLoading, trialDays, subInfo, trialQuantity, trialPlanQuantity, users]);

  const fetchData = async (isSocket) => {
    try {
      if (!isSocket) setIsLoading(true)
      await dispatch(fetchShopifyUser()).unwrap().catch(err => console.log(err))
      const allItemsCount = await dispatch(fetchShopifyAll()).unwrap()
      setBackupStatus(allItemsCount?.backupStatus || 'initialized');
      const newRow: any = [];
      const nameItem = {
        ordersCount: 'orders',
        customersCount: 'customers',
        productsCount: 'products'
      }
      Object.keys(allItemsCount?.entities).forEach((item, index) => {
        newRow.push({
          id: index + item,
          name: nameItem[item],
          amount: allItemsCount?.entities[item],
          path: nameItem[item]
        })
      })
      setRows(newRow);
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false)
    }
  };

  useEffect(() => {
    if (platformInfo.subPlan.type === ESubscriptionPlans.NO_SUB && trialState === ETrialStatus.NOT_STARTED) {
      navigate('/app-connector')
    }
    fetchData();
    onMessage((data) => {
      if (data.platform === EPlatformsNames.SHOPIFY && data.type === 'backup') {
        if (data.status === 'initialized') {
          setBackupStatus('process')
        } else if (data.status === 'success') {
          fetchData(true)
          setBackupStatus('success')
          setProgressEnd(true)
        } else if (data.status === 'failed' || data.status === 'error') {
          fetchData(true)
          setBackupStatus('failed')
          setProgressEnd(true)
        }
      }
      if (data.platform === EPlatformsNames.SHOPIFY && data.type === 'restore') {
        if (data.status === 'initialized') {
          setProgressItem(translate(`notifications.all.actionBadge.many.restore`, {app: 'Shopify'}))
        } else if (data.status === 'success' || data.status === 'failed' || data.status === 'error') {
          fetchData(true)
          setProgressEnd(true)
        }
      }
    });
  }, []);

  useEffect(() => {
    if (isUserSub && !roleApp?.[platformName]) {
      navigate('/', {replace: true})
    }
  }, [isUserSub, roleApp]);

  useEffect(() => {
    if ((platformInfo.subPlan.type === ESubscriptionPlans.TRIAL && !platformInfo.subPlan.periodHours) ||
      (platformInfo.subPlan.type === ESubscriptionPlans.SUBSCRIPTION && !platformInfo.subPlan.isPaid)) {
      setPaymentState(
        platformInfo.subPlan.type === ESubscriptionPlans.TRIAL && !platformInfo.subPlan.periodHours ?
          EPaymentRequiredType.TRIAL : platformInfo.subPlan.isPaid ?
            EPaymentRequiredType.NON : EPaymentRequiredType.SUB,
      );
    } else {
      setPaymentState(EPaymentRequiredType.NON)
    }

    if (!isUserSub && platformInfo.subPlan.isPaid !== undefined && !platformInfo.subPlan.isPaid && !buyShowed) {
      setBuyShowed(true)
      Swal.fire({
        title: translate('notifications.subscription.subscribe_buy'),
        text: translate('notifications.subscription.subscribe_buy_text'),
        cancelButtonText: translate('notifications.choices.close'),
        confirmButtonText: translate('notifications.choices.buy_subscription_now'),
        showCancelButton: true,
        imageUrl: errorBigIcon,
      })
        .then((res) => {
          if (res.isConfirmed) {
            navigate('/platform/dashboard/shopify-prices-page')
          }
        })
    }
  }, [platformInfo]);

  useEffect(() => {
    if (!isLoading && trialState === 'expired' && (!subInfo || subInfo?.status !== 'active') && !buyShowed) {
      setBuyShowed(true)
      Swal.fire({
        title: !!subInfo ? translate('notifications.subscription.subscribe_buy') : translate('notifications.subscription.subscribe_buy_trial'),
        text: !!subInfo ? translate('notifications.subscription.subscribe_buy_text') : translate('notifications.subscription.subscribe_buy_text_trial'),
        cancelButtonText: translate('notifications.choices.close'),
        confirmButtonText: translate('notifications.choices.buy_subscription_now'),
        showCancelButton: true,
        imageUrl: errorBigIcon,
      })
        .then((res) => {
          if (res.isConfirmed) {
            navigate('/platform/dashboard/shopify-prices-page')
          }
        })
    }
  }, [isLoading, subInfo]);

  const closeTrialPoppup = useCallback(() => {
    setPaymentPoppup({...paymentPoppup, isOpen: false});
  }, []);

  const backupAllNow = async () => {
    try {
      if (!isTestUser) {
        setUserCanBackup(false)
      }
      setBackupStatus('process')

      await dispatch(backupShopifyItem()).unwrap();
      setProgressItem(translate(`notifications.all.actionBadge.many.backup`, {app: 'Shopify'}))

      await Swal.fire({
        title: translate('notifications.titles.success'),
        text: platformInfo?.filteredItems && platformInfo.filteredItems?.length < 2 ? translate('notifications.github_repos.single_start_backup') : translate('notifications.github_repos.multi_start_backup'),
        toast: true,
        position: 'top-end',
        timerProgressBar: true,
        showConfirmButton: false,
        showCloseButton: true,
        imageUrl: toastSuccess,
        timer: 3000,
      });
    } catch (err) {
      const error = err as IError;

      await Swal.fire({
        title: translate('notifications.titles.error'),
        text: decoratePopUpMessage(error.error as string),
        imageUrl: errorBigIcon,
        confirmButtonText: translate('notifications.choices.close'),
      });
    }
  }

  const finishRestoreAll = async () => {
    try {
      if (!isTestUser) {
        setUserCanRestore(false)
      }
      setProgressItem(translate(`notifications.all.actionBadge.many.restore`, {app: 'Shopify'}))
      await fetchData()
      await Swal.fire({
        title: translate('notifications.titles.success'),
        text: 'Your restores are now in process',
        toast: true,
        position: 'top-end',
        timerProgressBar: true,
        showConfirmButton: false,
        showCloseButton: true,
        imageUrl: toastSuccess,
        timer: 3000,
      });
    } catch (err) {
      const error = err as IError;
      await Swal.fire({
        title: translate('notifications.titles.error'),
        text: decoratePopUpMessage(error.error as string),
        imageUrl: errorBigIcon,
        confirmButtonText: translate('notifications.choices.close'),
      });
    }
  }

  const restoreAllNow = async () => {
    Swal.fire({
      title: 'Loading backup dates...',
      html: '<div class="swal2-spinner"></div>',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      imageUrl: iconNotifyWarningInfo,
      showCancelButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });
    try {
      const backupDates = await dispatch(getShopifyBackupsDate()).unwrap();

      if (!backupDates || backupDates.length === 0) {
        Swal.fire({
          title: 'No backup dates found',
          text: 'Please try again later or contact support.',
          imageUrl: iconNotifyAlertTriangle,
          confirmButtonText: 'OK'
        });
        return;
      }

      const inputOptions = {}
      backupDates.forEach((item) => {
        inputOptions[item] = item;
      });

      const result = await Swal.fire({
        title: 'Choose backup date for restore',
        imageUrl: iconNotifyWarningInfo,
        input: 'select',
        inputOptions: inputOptions,
        inputPlaceholder: 'Select a date',
        showCancelButton: true,
        confirmButtonText: 'Restore',
        cancelButtonText: 'Cancel',
        focusConfirm: false,
        allowOutsideClick: true,
        allowEscapeKey: true,
        showConfirmButton: true,
        inputValidator: (value) => {
          if (!value) {
            return 'You need to select a backup date!';
          }
        },
        preConfirm: async (selectedDate) => {
          try {
            console.log(selectedDate);
            Swal.disableButtons();
            Swal.showLoading();
            await dispatch(restoreAllShopifyBackup(selectedDate)).unwrap();
            await finishRestoreAll()
            return selectedDate;
          } catch (err) {
            const error = err as IError;

            await Swal.fire({
              title: translate('notifications.titles.error'),
              text: error?.error ? decoratePopUpMessage(error.error as string) : 'An unknown error occurred.',
              imageUrl: errorBigIcon,
              confirmButtonText: translate('notifications.choices.close'),
            });
            return false;
          }
        },
      });

      if (result.dismiss) {
        return
      }
    } catch (err) {
      const error = err as IError;

      await Swal.fire({
        title: translate('notifications.titles.error'),
        text: decoratePopUpMessage(error.error as string),
        imageUrl: errorBigIcon,
        confirmButtonText: translate('notifications.choices.close'),
      });
    }
  }

  const columns = [
    {
      field: 'name',
      headerName: 'Type',
      flex: 0.7,
      editable: false,
      sortable: false,
      cellClassName: 'items-table-cell--name',
      headerClassName: 'items-table--header',
      renderCell: (params: GridCellParams) => {
        const data = params.value.split('::')
        return (
          <CustomTooltip
            title={
              params.row.description ?
                `${data[0]} - ${params.row.description}` :
                params.row.typeManage ?
                  `${data[0]} - ${params.row.typeManage}` :
                  data[0]
            }
          >
            <div className="name-block">
              <img src={Platform[platformName as EPlatformsNames]?.smallLogo} className="platformLogo" loading="lazy"/>
              {data[1] && <span className='item-private-type'>{data[1]}</span>}
              <div>
                <span style={{fontWeight: 500}}>{data[0]}</span> <br/>
                {params.row.description}
              </div>
            </div>
          </CustomTooltip>
        );
      },
    },
    {
      field: 'amount',
      headerName: 'Amount',
      minWidth: 120,
      maxWidth: 200,
      flex: 0.08,
      editable: false,
      sortable: false,
      headerClassName: 'items-table--header',
      renderCell: (params: GridCellParams) => {
        return (
          <div className="date-block">
            {params.value}
          </div>
        );
      },
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 144,
      editable: false,
      sortable: false,
      headerClassName: 'items-table--header',
      renderCell: (params: GridCellParams) => (
        (trialState === 'active' || subInfo?.status === 'active') ?
          <Chip
            className={(backupStatus === 'initialized' || backupStatus === 'process') ? 'process active-process' : 'success'}
            label={
              backupStatus === 'initialized' ?
                translate('subscriptions.statuses.initialized') :
                backupStatus === 'process' ?
                  translate('notifications.all.process.backing_up_process') :
                  translate('subscriptions.statuses.active')}
          /> :
          <Chip
            className={'error'}
            label={translate('subscriptions.statuses.inactive')}
          />
      ),
    },
    {
      field: 'id',
      headerName: '',
      width: 136,
      // flex: 1,
      editable: false,
      sortable: false,
      headerClassName: 'items-table--header',
      valueGetter: () => '',
      renderCell: (params: GridCellParams) => (
        <div
          className="action-buttons platforms-btn table-actions"
          style={{visibility: 'visible'}}
        >
          <Button
            variant="outlined" color="primary"
            disabled={!params.row.amount}
            // disabled={(!platformInfo.subPlan?.isPaid || (platformInfo.subPlan.type === ESubscriptionPlans.TRIAL && trialState === ETrialStatus.EXPIRED))}
            onClick={() => {
              navigate(`/platform/dashboard/shopify/${params.row.path}`)
            }}
          >
            {translate('common.buttons.view')}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div id="platform-content" className='list-view shopify-all-view'>
      {iniFrame() &&
        <BadgesIFrameMode/>
      }
      {trialState === 'active' && trialDays && (
        <BadgesTrial2 trialDays={trialDays} platformName={platformName}/>
      )}
      {!isLoading && trialState === 'expired' && (!subInfo || subInfo?.status !== 'active') && (
        <BadgesTrial2 isExpired={true} platformName={platformName} color={'error'} isSub={!!subInfo}/>
      )}
      {subInfo && subInfo?.isCancel && subInfo?.finishDateInMillisecond && ((subInfo?.finishDateInMillisecond - Date.now()) / (24 * 60 * 60 * 1000)) < 1 && (
        <BadgesSubscription platformName={platformName}/>
      )}
      {!!progressItem?.length && (
        <BackupBadges progressItem={progressItem} platformName={platformName} isComplete={progressEnd} onClose={() => {
          setProgressItem('')
        }}/>
      )}
      {appWasDeleted && (
        <BadgesAppWasDeleted platformName={platformName} redirectUrl={redirectUrl || ''}/>
      )}
      <WhiteCard>
        <div className="header-block">
          <Typography variant="h2" gutterBottom display="inline" mr={6} mb={0}>
            <img src={platform.smallLogo} alt={platform.title} loading="lazy" className="platform-logo"/>
            {translate('forms.github_add_edit_repo.your_items', {platform: platform?.title, itemName: ''})}
          </Typography>
          <div className="user-name">
            {platformInfo && !!platformInfo.users?.length && (
              <Typography variant="h4" gutterBottom display="inline">
                @{platformInfo.users[0].username}
              </Typography>
            )}
          </div>

          <div className="search-block">
            <div className="action-button">
              {(trialState === 'active' || subInfo || !!platformInfo?.users?.length) && (
                <CustomTooltip
                  title={(platformInfo.subPlan?.name !== 'Pro' && platformInfo.subPlan?.name !== 'Trial') ? 'This feature is only available with a Pro Plan subscription' : ''}
                >
                  <Button variant="outlined" color="primary" mr={2}
                          disabled={platformInfo.subPlan?.name !== 'Pro' && platformInfo.subPlan?.name !== 'Trial' || paymentState !== EPaymentRequiredType.NON}
                          onClick={() => setShowSettings2(true)}>
                    <ExtStoreIco className='feather-icon-in-button'/>
                    External storage
                  </Button>
                </CustomTooltip>
              )}
              <Button
                mr={2} variant="outlined" color="primary"
                onClick={() => (platformInfo.subPlan?.name === 'Pro' || platformInfo.subPlan?.name === 'Trial') ? restoreAllNow() : updateSubscriptionPlanModal({
                  translate,
                  platformName,
                  navigate
                })}
                disabled={(platformInfo.subPlan.type === ESubscriptionPlans.NO_SUB && trialState === ETrialStatus.NOT_STARTED) ||
                  (platformInfo.subPlan.type === ESubscriptionPlans.TRIAL && trialState === ETrialStatus.EXPIRED) ||
                  paymentState !== EPaymentRequiredType.NON || !isAdminRole}
              >
                <DatabaseIcoInvert className="feather-icon-in-button"/>
                {translate('views.platform_pages.restore_all')}
              </Button>
              {((userCanBackup || isTestUser) ? (
                <CustomTooltip
                  title={(!isTestUser && platformInfo.subPlan?.name !== 'Pro' && platformInfo.subPlan?.name !== 'Trial') ? 'This feature is only available with a Pro Plan subscription' : ''}>
                  <Button
                    mr={2} variant="contained" color="secondary"
                    onClick={() => (isTestUser || platformInfo.subPlan?.name === 'Pro' || platformInfo.subPlan?.name === 'Trial') ? backupAllNow() : updateSubscriptionPlanModal({
                      translate,
                      platformName,
                      navigate
                    })}
                    disabled={!!progressItem?.length || (!isTestUser && ((platformInfo.subPlan.type === ESubscriptionPlans.NO_SUB && trialState === ETrialStatus.NOT_STARTED) ||
                      (platformInfo.subPlan.type === ESubscriptionPlans.TRIAL && trialState === ETrialStatus.EXPIRED) ||
                      paymentState !== EPaymentRequiredType.NON)) || !isAdminRole}
                  >
                    <Play size={16} style={{marginRight: '4px'}}/>
                    {translate('views.platform_pages.backupNow')}
                  </Button>
                </CustomTooltip>
              ) : (
                <CustomTimerTooltip setUserCanBackup={() => setUserCanBackup(true)}>
                  <Button
                    mr={2} variant="contained" color="secondary"
                    onClick={() => (isTestUser || platformInfo.subPlan?.name === 'Pro' || platformInfo.subPlan?.name === 'Trial') ? backupAllNow() : updateSubscriptionPlanModal({
                      translate,
                      platformName,
                      navigate
                    })}
                    disabled={true}
                  >
                    <Play size={16} style={{marginRight: '4px'}}/>
                    {translate('views.platform_pages.backupNow')}
                  </Button>
                </CustomTimerTooltip>
              ))}
            </div>
          </div>

        </div>
        <div className="header-block header-block-sub">

        </div>
        <Box sx={{width: '100%'}}>
          {isLoadingTrelloUsers || isLoading || userLoading ?
            <div className="spinner-wrapper">
              <CircularProgress/>
            </div>
            :
            <div className="data-block">
              {(platformInfo.subPlan.type === ESubscriptionPlans.NO_SUB && trialState === ETrialStatus.NOT_STARTED) ||
              (platformInfo.subPlan.type === ESubscriptionPlans.TRIAL && trialState === ETrialStatus.EXPIRED && !rows?.length) ?
                <NoSubscriptionBlock platformName={platformName} isOwner={!isUserSub}/>
                : !users?.length || !users[0]?.username ?
                  <NoBackupsBlock
                    isAdmin={!isUserSub}
                    action={() => navigate('/app-connector')}
                    platformItem={translate('views.bulk_restore.itemName.trello.one')}
                    actionText={translate('forms.github_add_edit_repo.configure_account')}
                  />
                  : !!users?.length && !hasUserApp ?
                    <UserNotConfirmedBlock noApp={true} isOwner={!isUserSub} isShopify={true}
                                           redirectUrl={redirectUrl}/>
                    : !!users?.length && !isShopConfirmed ?
                      <UserNotConfirmedBlock isOwner={!isUserSub} isShopify={true} reloadData={fetchData}/>
                      : <>
                        <div className="repo-wrapper">
                          <div className="repos-list">
                            {isLoading ?
                              <div className="spinner-wrapper">
                                <CircularProgress/>
                              </div> :
                              <DataGrid
                                loading={isLoading}
                                rows={rows}
                                columns={columns}
                                rowHeight={64}
                                disableColumnMenu
                                disableColumnFilter
                                disableDensitySelector
                                disableRowSelectionOnClick
                                disableColumnSelector
                                hideFooter
                                autoHeight
                              />
                            }
                          </div>
                        </div>
                      </>
              }
              <PaymentRequiredPoppup
                isOpen={paymentPoppup.isOpen}
                closeNotification={closeTrialPoppup}
                type={paymentState}
                platformName={paymentPoppup.platformName}
              />
              <Modal
                isOpen={showSettings}
                closeNotification={() => setShowSettings(false)}
                title='Edit settings'
                child={<NotionUserAddEdit
                  user={users ? users[0] : {}}
                  subPlan={platformInfo.subPlan}
                />}
              />
              <Modal
                isOpen={showSettings2}
                className='ext-storage-modal'
                closeNotification={() => setShowSettings2(false)}
                child={<ExternalStorage
                  platformModal='shopify'
                  closeModal={() => setShowSettings2(false)}
                />}
              />
            </div>
          }
        </Box>
      </WhiteCard>
      <NotionTutorial isOpen={isOpenTutorial} closeNotification={() => setIsOpenTutorial(false)}/>
    </div>
  );
};

export default withRedirectAfterLogout()(ShopifyPagesListView);
